import { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Globe2, Search, X } from 'lucide-react';
import { getCountries } from '../../../../../api/geo/useCountries';
import { getCities } from '../../../../../api/geo/useCities';
import styles from '../HomepageSettings.module.css';

// ISO alpha-2 → flag emoji. Countries carry an isoCode; cities do not, so those
// get a pin glyph instead of a (possibly wrong) flag.
const flagEmoji = (iso) => {
  const cc = String(iso || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  const BASE = 0x1f1e6;
  return (
    String.fromCodePoint(BASE + cc.charCodeAt(0) - 65) +
    String.fromCodePoint(BASE + cc.charCodeAt(1) - 65)
  );
};

// The key a picked destination is de-duplicated by — must match the backend's
// `${type}:${CODE}` so the picker hides exactly what the normalizer would drop.
export const destKey = (d) => `${d?.type}:${String(d?.code || '').trim().toUpperCase()}`;

// Human label for a chip / the website's `destinationLabel` query param.
export const destLabel = (d) =>
  d?.type === 'city' && d?.countryName ? `${d.name}, ${d.countryName}` : d?.name || d?.code || '';

/**
 * Search countries and cities together and pick one. `onPick` receives a
 * normalized { type, code, name, countryName } ready to store on the CMS item.
 *
 * The stored `code` is the Hotelbeds code (Country.code / Destination.code) —
 * the exact value the website results page filters by.
 */
export default function DestinationPicker({ onPick, disabled, excludeKeys }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  // Guards against out-of-order responses when the query changes mid-flight.
  const reqIdRef = useRef(0);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const myId = ++reqIdRef.current;
    setLoading(true);
    const t = setTimeout(() => {
      // Both lists in one shot; a failing side degrades to the other rather than
      // blanking the dropdown.
      Promise.allSettled([
        getCountries({ search: q, limit: 8, active: true }),
        getCities({ search: q, limit: 12, active: true }),
      ])
        .then(([cRes, dRes]) => {
          if (cancelled || myId !== reqIdRef.current) return;
          const countries = (cRes.status === 'fulfilled' && Array.isArray(cRes.value?.data) ? cRes.value.data : [])
            .filter((c) => c?.code)
            .map((c) => ({
              type: 'country',
              code: String(c.code).trim().toUpperCase(),
              name: c.name || c.code,
              countryName: '',
              isoCode: c.isoCode || '',
              meta: `Country · ${c.code}${c.cityCount ? ` · ${c.cityCount} cities` : ''}`,
            }));
          const cities = (dRes.status === 'fulfilled' && Array.isArray(dRes.value?.data) ? dRes.value.data : [])
            .filter((d) => d?.code)
            .map((d) => ({
              type: 'city',
              code: String(d.code).trim().toUpperCase(),
              name: d.name || d.code,
              countryName: d.countryName || '',
              isoCode: '',
              meta: `City · ${d.code}${d.countryName ? ` · ${d.countryName}` : ''}`,
            }));
          setResults([...countries, ...cities]);
          setOpen(true);
        })
        .finally(() => {
          if (!cancelled && myId === reqIdRef.current) setLoading(false);
        });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // `excludeKeys` is deliberately NOT a dependency: the caller rebuilds that Set
    // on every render, so including it would restart this debounce (and refire the
    // search) on every keystroke anywhere in the CMS form. Already-picked entries
    // are filtered out at render time instead.
  }, [query]);

  const visible = useMemo(() => {
    const exclude = excludeKeys instanceof Set ? excludeKeys : new Set(excludeKeys || []);
    return results.filter((r) => !exclude.has(destKey(r)));
  }, [results, excludeKeys]);

  const hint = useMemo(() => {
    if (disabled) return '';
    if (loading) return 'Searching…';
    const q = query.trim();
    if (q.length > 0 && q.length < 2) return 'Type at least 2 characters';
    if (q.length >= 2 && visible.length === 0) return 'No countries or cities found';
    return '';
  }, [disabled, loading, query, visible.length]);

  const choose = (r) => {
    onPick({ type: r.type, code: r.code, name: r.name, countryName: r.countryName });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className={styles.hotelPicker} ref={boxRef}>
      <div className={styles.hotelPickerInput}>
        <Search size={14} />
        <input
          value={query}
          disabled={disabled}
          placeholder={disabled ? 'Maximum destinations reached' : 'Search a country or city…'}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => visible.length && setOpen(true)}
        />
        {query && (
          <button
            type="button"
            className={styles.hotelPickerClear}
            onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
          >
            <X size={13} />
          </button>
        )}
      </div>
      {hint && <span className={styles.fieldHint}>{hint}</span>}
      {open && visible.length > 0 && (
        <div className={styles.hotelPickerDropdown}>
          {visible.map((r) => (
            <button
              key={destKey(r)}
              type="button"
              className={styles.hotelPickerOption}
              onClick={() => choose(r)}
            >
              <span className={`${styles.destGlyph} ${r.type === 'country' ? styles.destGlyphCountry : styles.destGlyphCity}`}>
                {r.type === 'country' && flagEmoji(r.isoCode)
                  ? flagEmoji(r.isoCode)
                  : r.type === 'country'
                    ? <Globe2 size={15} />
                    : <Building2 size={15} />}
              </span>
              <span className={styles.hotelPickerText}>
                <span className={styles.hotelPickerName}>{r.name}</span>
                <span className={styles.hotelPickerMeta}>{r.meta}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
